import { createContext, ComponentChildren } from 'preact';
import { useContext, useMemo } from 'preact/hooks';

interface LocaleCtx {
  // Requested BCP 47 tag. resolve() in @goalpost/content matches it
  // to a shipped locale (exact, then language-prefix, then default).
  locale: string;
}

const FALLBACK_LOCALE = 'en-US';

// The marketing site has no authenticated user, so locale comes straight
// from the browser's ranked preference list (the same data Accept-Language
// would carry, without a server round-trip). The product app additionally
// layers a signed-in user's persisted locale on top; that path lives in the
// webapp's own useLocale and isn't relevant here.
function detectLocale(): string {
  if (typeof navigator === 'undefined') return FALLBACK_LOCALE;
  return navigator.languages?.[0] ?? navigator.language ?? FALLBACK_LOCALE;
}

const LocaleContext = createContext<LocaleCtx | null>(null);

export function LocaleProvider({ children }: { children: ComponentChildren }) {
  const value = useMemo<LocaleCtx>(() => ({ locale: detectLocale() }), []);
  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleCtx {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
